import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfigService, GridBaseQuery, GridHelperService } from '@core';
import { KendoDataSourceResultDto } from '@shared';
import { InvestigationTypeResponseDto } from '../dtos/investigation-type-response.dto';
import { InvestigationTypeDetailResponseDto } from '../dtos/investigation-type-detail-response.dto';
import { InvestigationTypeCategoryResponseDto } from '../dtos/investigation-type-category-response.dto';
import { CreateInvestigationTypeRequestDto } from '../dtos/create-investigation-type-request.dto';
import { UpdateInvestigationTypeRequestDto } from '../dtos/update-investigation-type-request.dto';
import { ToggleInvestigationTypeActiveRequestDto } from '../dtos/toggle-investigation-type-active-request.dto';
import { ChecklistResponseDto } from '../dtos/checklist-response.dto';
import { ChecklistListItemResponseDto } from '../dtos/checklist-list-item-response.dto';
import { SaveChecklistRequestDto } from '../dtos/save-checklist-request.dto';
import { ToggleChecklistDisplayRequestDto } from '../dtos/toggle-checklist-display-request.dto';
import { InvestigationTypeDeletionImpactResponseDto } from '../dtos/investigation-type-deletion-impact-response.dto';

@Injectable({ providedIn: 'root' })
export class InvestigationTypeApi {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _gridHelper = inject(GridHelperService);
  private readonly _baseUrl = `${this._config.versionedApiUrl}/investigations/investigation-types`;

  public getAllInvestigationTypes(): Observable<KendoDataSourceResultDto<InvestigationTypeResponseDto>> {
    return this._http.get<KendoDataSourceResultDto<InvestigationTypeResponseDto>>(this._baseUrl);
  }

  public getActiveInvestigationTypes(): Observable<InvestigationTypeResponseDto[]> {
    return this._http.get<InvestigationTypeResponseDto[]>(`${this._baseUrl}/active`);
  }

  public getInvestigationTypesWithPaging(query: GridBaseQuery): Observable<KendoDataSourceResultDto<InvestigationTypeResponseDto>> {
    const params = this._gridHelper.buildGridParams(query);
    return this._http.get<KendoDataSourceResultDto<InvestigationTypeResponseDto>>(this._baseUrl, { params });
  }

  public getInvestigationTypeById(id: string): Observable<InvestigationTypeDetailResponseDto> {
    return this._http.get<InvestigationTypeDetailResponseDto>(`${this._baseUrl}/${id}`);
  }

  public getCategories(): Observable<InvestigationTypeCategoryResponseDto[]> {
    return this._http.get<InvestigationTypeCategoryResponseDto[]>(`${this._baseUrl}/categories`);
  }

  public createInvestigationType(request: CreateInvestigationTypeRequestDto): Observable<InvestigationTypeDetailResponseDto> {
    return this._http.post<InvestigationTypeDetailResponseDto>(this._baseUrl, request);
  }

  public updateInvestigationType(id: string, request: UpdateInvestigationTypeRequestDto): Observable<InvestigationTypeDetailResponseDto> {
    return this._http.put<InvestigationTypeDetailResponseDto>(`${this._baseUrl}/${id}`, request);
  }

  public deleteInvestigationType(id: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}`);
  }

  public toggleInvestigationTypeActive(id: string, request: ToggleInvestigationTypeActiveRequestDto): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/toggle-active`, request);
  }

  public getChecklist(investigationTypeId: string): Observable<ChecklistResponseDto> {
    return this._http.get<ChecklistResponseDto>(`${this._baseUrl}/${investigationTypeId}/checklist`);
  }

  public saveChecklist(investigationTypeId: string, request: SaveChecklistRequestDto): Observable<ChecklistResponseDto> {
    return this._http.post<ChecklistResponseDto>(`${this._baseUrl}/${investigationTypeId}/checklist`, request);
  }

  public getAllChecklists(): Observable<KendoDataSourceResultDto<ChecklistListItemResponseDto>> {
    return this._http.get<KendoDataSourceResultDto<ChecklistListItemResponseDto>>(`${this._baseUrl}/checklists`);
  }

  public deleteChecklist(investigationTypeId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${investigationTypeId}/checklist`);
  }

  public toggleChecklistDisplay(investigationTypeId: string, request: ToggleChecklistDisplayRequestDto): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${investigationTypeId}/checklist/toggle-display-on-cases`, request);
  }

  public getInvestigationTypeDeletionImpact(id: string): Observable<InvestigationTypeDeletionImpactResponseDto> {
    return this._http.get<InvestigationTypeDeletionImpactResponseDto>(`${this._baseUrl}/${id}/deletion-impact`);
  }
}
